import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { ActionIcon, Autocomplete, Box, Button, Divider, FileButton, Group, Text, Tooltip } from '@mantine/core'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import Underline from '@tiptap/extension-underline'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import {
  IconBold,
  IconH2,
  IconH3,
  IconItalic,
  IconLink,
  IconList,
  IconListNumbers,
  IconPhoto,
  IconQuote,
  IconStrikethrough,
  IconUnderline,
} from '@/components/icons'
import { api, ApiError } from '@/lib/api'
import { showNotification } from '@mantine/notifications'

type Props = {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  minHeight?: number
  disabled?: boolean
  articleTitles?: string[]
}

function plainToHtml(text: string): string {
  const trimmed = (text || '').trim()
  if (!trimmed) return '<p></p>'
  if (/<\s*(p|br|ul|ol|li|h[1-4]|img|strong|em|blockquote|pre|div)\b/i.test(trimmed)) {
    return trimmed
  }
  return trimmed
    .split(/\n{2,}/)
    .map((block) => `<p>${block.replace(/\n/g, '<br>')}</p>`)
    .join('')
}

export function KnowledgeRichTextEditor({
  value,
  onChange,
  placeholder = 'Текст инструкции, шаблон ответа или скрипт…',
  minHeight = 220,
  disabled = false,
  articleTitles = [],
}: Props) {
  const [articleTitle, setArticleTitle] = useState('')
  // Different materials can share a title, but Mantine requires unique option values.
  const articleTitleOptions = useMemo(() => [...new Set(articleTitles)], [articleTitles])
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        link: false,
        underline: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        protocols: ['kb-article'],
      }),
      Image.configure({
        inline: false,
        allowBase64: false,
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: plainToHtml(value),
    editable: !disabled,
    immediatelyRender: false,
    onUpdate: ({ editor: ed }) => {
      onChangeRef.current(ed.getHTML())
    },
  })

  useEffect(() => {
    if (!editor || editor.isDestroyed) return
    editor.setEditable(!disabled)
  }, [editor, disabled])

  useEffect(() => {
    if (!editor || editor.isDestroyed) return
    try {
      const next = plainToHtml(value)
      const current = editor.getHTML()
      if (next !== current) {
        editor.commands.setContent(next, { emitUpdate: false })
      }
    } catch {
      /* редактор уже размонтирован */
    }
  }, [editor, value])

  const insertImage = useCallback(
    async (file: File | null) => {
      if (!file || !editor) return
      if (!file.type.startsWith('image/')) {
        showNotification({ title: 'Файл', message: 'Выберите изображение', color: 'yellow' })
        return
      }
      try {
        const uploaded = await api.supportKnowledgeUploadImage(file)
        const src = uploaded.path || uploaded.url
        editor.chain().focus().setImage({ src, alt: file.name }).run()
      } catch (e) {
        showNotification({
          title: 'Ошибка загрузки',
          message: e instanceof ApiError ? e.message : 'Не удалось загрузить изображение',
          color: 'red',
        })
      }
    },
    [editor],
  )

  const setLink = useCallback(() => {
    if (!editor) return
    const prev = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('Ссылка', prev || 'https://')
    if (url === null) return
    if (!url.trim()) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run()
  }, [editor])

  const insertArticleReference = useCallback(
    (mode: 'related' | 'next') => {
      if (!editor) return
      const title = articleTitle.trim()
      if (!title) return
      const href = `kb-article:${encodeURIComponent(title)}`
      const html =
        mode === 'related'
          ? `<ul><li><a href="${href}">${title}</a></li></ul>`
          : `<p>Далее: <a href="${href}">${title}</a></p>`
      editor.chain().focus().insertContent(html).run()
      setArticleTitle('')
    },
    [articleTitle, editor],
  )

  if (!editor) return null

  const btn = (active: boolean, label: string, onClick: () => void, icon: ReactNode) => (
    <Tooltip label={label}>
      <ActionIcon
        variant={active ? 'filled' : 'subtle'}
        color={active ? 'blue' : 'gray'}
        size="sm"
        onClick={onClick}
        disabled={disabled}
      >
        {icon}
      </ActionIcon>
    </Tooltip>
  )

  return (
    <Box
      style={{
        border: '1px solid var(--mantine-color-default-border)',
        borderRadius: 'var(--mantine-radius-md)',
        overflow: 'hidden',
        opacity: disabled ? 0.7 : 1,
      }}
    >
      <Group gap={4} p={6} wrap="wrap" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
        {btn(
          editor.isActive('bold'),
          'Жирный',
          () => editor.chain().focus().toggleBold().run(),
          <IconBold size={16} stroke={1.5} />,
        )}
        {btn(
          editor.isActive('italic'),
          'Курсив',
          () => editor.chain().focus().toggleItalic().run(),
          <IconItalic size={16} stroke={1.5} />,
        )}
        {btn(
          editor.isActive('underline'),
          'Подчёркивание',
          () => editor.chain().focus().toggleUnderline().run(),
          <IconUnderline size={16} stroke={1.5} />,
        )}
        {btn(
          editor.isActive('strike'),
          'Зачёркнутый',
          () => editor.chain().focus().toggleStrike().run(),
          <IconStrikethrough size={16} stroke={1.5} />,
        )}
        <Divider orientation="vertical" />
        {btn(
          editor.isActive('heading', { level: 2 }),
          'Заголовок 2',
          () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
          <IconH2 size={16} stroke={1.5} />,
        )}
        {btn(
          editor.isActive('heading', { level: 3 }),
          'Заголовок 3',
          () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
          <IconH3 size={16} stroke={1.5} />,
        )}
        <Divider orientation="vertical" />
        {btn(
          editor.isActive('bulletList'),
          'Маркированный список',
          () => editor.chain().focus().toggleBulletList().run(),
          <IconList size={16} stroke={1.5} />,
        )}
        {btn(
          editor.isActive('orderedList'),
          'Нумерованный список',
          () => editor.chain().focus().toggleOrderedList().run(),
          <IconListNumbers size={16} stroke={1.5} />,
        )}
        {btn(
          editor.isActive('blockquote'),
          'Цитата',
          () => editor.chain().focus().toggleBlockquote().run(),
          <IconQuote size={16} stroke={1.5} />,
        )}
        <Divider orientation="vertical" />
        {btn(editor.isActive('link'), 'Ссылка', setLink, <IconLink size={16} stroke={1.5} />)}
        <FileButton onChange={(f) => void insertImage(f)} accept="image/png,image/jpeg,image/gif,image/webp">
          {(props) => (
            <Tooltip label="Вставить изображение">
              <ActionIcon {...props} variant="subtle" color="gray" size="sm" disabled={disabled}>
                <IconPhoto size={16} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
          )}
        </FileButton>
      </Group>
      <Box p="xs" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
        <Text size="xs" c="dimmed" mb={6}>
          Связи между статьями: начните вводить заголовок, выберите существующую статью и вставьте ссылку.
        </Text>
        <Group gap="xs" align="flex-end">
          <Autocomplete
            size="xs"
            style={{ flex: 1, minWidth: 220 }}
            label="Статья для перехода"
            placeholder="Например: Макрос: проверить оба режима"
            data={articleTitleOptions}
            value={articleTitle}
            onChange={setArticleTitle}
            limit={8}
            disabled={disabled}
          />
          <Button
            size="compact-sm"
            variant="light"
            disabled={disabled || !articleTitle.trim()}
            onClick={() => insertArticleReference('related')}
          >
            В связанные
          </Button>
          <Button
            size="compact-sm"
            variant="light"
            disabled={disabled || !articleTitle.trim()}
            onClick={() => insertArticleReference('next')}
          >
            В следующий шаг
          </Button>
        </Group>
      </Box>
      <Box
        px="sm"
        py="xs"
        style={{
          minHeight,
          maxHeight: 420,
          overflowY: 'auto',
        }}
        className="kb-rich-editor"
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  )
}
